import { useState } from 'react';
import { Box, Menu, MenuItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { CONTROLE_ALTURA, CONTROLE_ALTURA_MOBILE, CONTROLE_RADIUS } from './listagemCellStyles';

export interface IOrdenarPorOpcao {
  nameBD: string;
  descricao: string;
}

interface IOrdenarPorControlProps {
  opcoes: IOrdenarPorOpcao[];
  valorSelecionado: string;
  onChange: (nameBD: string) => void;
  label?: string;
  // Default 260 (usado nas listagens normais) — telas com mais controles na mesma barra (ex.:
  // DialogVenda, que tem os dois seletores + o campo de busca lado a lado) podem passar um
  // valor menor pra caber tudo sem apertar o campo de busca.
  minWidth?: number;
}

// Seletor de campo/critério de busca das listagens. Implementado como Box+Menu, não
// FormControl+Select: o Select "outlined" sem um InputLabel de verdade deformava a borda ao
// focar/abrir (o mecanismo de "notch" do MUI, pensado pra abrir espaço pro label flutuante,
// ainda agia mesmo sem label nenhum) — Box+Menu dá controle total do formato da caixa sem
// depender desse mecanismo (mesmo padrão já usado no menu de tema da sidebar).
export const OrdenarPorControl: React.FC<IOrdenarPorControlProps> = ({
  opcoes,
  valorSelecionado,
  onChange,
  label = 'Ordenar por',
  minWidth = 260,
}) => {
  const theme = useTheme();
  // No celular o controle fica mais baixo (CONTROLE_ALTURA_MOBILE) e sem o minWidth fixo
  // (que forçava cada seletor pra própria linha) — quem decide a largura na tela pequena é
  // o container flex de quem usa este componente (ex.: dois seletores lado a lado).
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selecionado = opcoes.find((item) => item.nameBD === valorSelecionado);

  return (
    <>
      <Box
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: smDown ? 0 : minWidth,
          flex: smDown ? 1 : undefined,
          height: `${smDown ? CONTROLE_ALTURA_MOBILE : CONTROLE_ALTURA}px`,
          px: smDown ? 1.5 : 2,
          borderRadius: CONTROLE_RADIUS,
          border: `1px solid ${theme.torq.border.default}`,
          backgroundColor: theme.torq.surface.card,
          cursor: 'pointer',
          transition: 'border-color 150ms ease',
          '&:hover': { borderColor: theme.torq.border.strong },
        }}
      >
        <SortRoundedIcon sx={{ fontSize: 20, color: theme.torq.text.muted, flexShrink: 0 }} />
        <Box display='flex' flexDirection='column' lineHeight={1.2} flex={1} minWidth={0}>
          <Typography sx={{ fontSize: '0.6875rem', color: theme.torq.text.muted }}>
            {label}
          </Typography>
          <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: theme.torq.text.primary }}>
            {selecionado?.descricao}
          </Typography>
        </Box>
        <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, color: theme.torq.text.muted, flexShrink: 0 }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {opcoes.map((item) => (
          <MenuItem
            key={item.nameBD}
            selected={item.nameBD === valorSelecionado}
            onClick={() => {
              onChange(item.nameBD);
              setAnchorEl(null);
            }}
          >
            {item.descricao}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
